import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useListData from '../../hooks/useListData'
import useDebounce from '../../hooks/useDebounce'
import useStaleTime from '../../hooks/useStaleTime'
import generateEndpoint from '../../utils/generateEndpoint'
import { getNestedValue } from '../../utils/objects/nestedValue'

const EMPTY_ROWS = []

export default function useGridListData(config, options = {}, useStore) {
    const { id } = useParams()

    const isCollectionMode = !!config.endpoint
    const isInlineMode = !!config.linesArray

    if (!isCollectionMode && !isInlineMode) {
        throw new Error(`GridList config must define either "endpoint" or "linesArray". Config key has neither.`)
    }

    const linesArray = config.linesArray
    const storePath = config.storePath || []

    if (isCollectionMode) {
        const searchTerm = ''
        const debouncedSearchTerm = useDebounce(searchTerm, 1000)
        const staleTime = useStaleTime(config.entity)
        const endpoint = generateEndpoint(config.endpoint, id)

        const { filterTerm, serverFilter } = useMemo(() => {
            const toPair = (f) => `${f.id}:${f.value === '{id}' ? id : f.value}`
            const currentFilter = config.filters?.list?.[0]
            const client = currentFilter?.applyFilter && currentFilter?.terms?.length
                ? currentFilter.terms.map(toPair).join(';')
                : false
            const configServer = currentFilter?.applyFilter && currentFilter?.serverTerms?.length
                ? currentFilter.serverTerms.map(toPair).join(';')
                : false
            const fixedServer = options?.fixedServerFilter || false
            const serverParts = [configServer, fixedServer].filter(Boolean)
            const server = serverParts.length ? serverParts.join(';') : false
            return { filterTerm: client, serverFilter: server }
        }, [config.filters, id, options?.fixedServerFilter])

        const result = useListData({
            endpoint,
            order: [config.orderBy, config.orderDirection || 'asc'],
            searchTerm: debouncedSearchTerm,
            filterTerm,
            serverFilter,
            staleTime,
            queryParams: config.queryParams,
        })

        const rows = useMemo(() => {
            if (!result?.data?.pages) return []
            return result.data.pages.flatMap(page =>
                Array.isArray(page?.data) ? page.data : []
            )
        }, [result?.data])

        return {
            rows,
            isLoading: result.isLoading,
            isError: result.isError,
            error: result.error,
            hasNextPage: result.hasNextPage,
            fetchNextPage: result.fetchNextPage,
            isFetchingNextPage: result.isFetchingNextPage,
            result,
            mode: 'collection',
            routeId: id,
        }
    }

    if (isInlineMode) {
        if (!useStore) {
            throw new Error('GridList inline-array mode requires a useStore prop.')
        }

        const [state, setState] = useStore(s => getNestedValue(s, storePath))
        const [fullState, setFullState] = useStore(s => s)
        const stateLines = state?.[linesArray]
        const rows = Array.isArray(stateLines) ? stateLines : EMPTY_ROWS

        return {
            rows,
            isLoading: false,
            isError: false,
            error: null,
            hasNextPage: false,
            fetchNextPage: () => {},
            isFetchingNextPage: false,
            result: null,
            mode: 'inline',
            setState,
            state,
            fullState,
            setFullState,
        }
    }
}
